import React from 'react';
import {
  Box, Paper, Typography, List, ListItem, ListItemText,
  Divider, Chip, Avatar, Stack, Card, CardContent, styled, alpha
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import RouteIcon from '@mui/icons-material/Route';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PlaceIcon from '@mui/icons-material/Place';
import LinkIcon from '@mui/icons-material/Link';
import Co2Icon from '@mui/icons-material/Co2';
import RemoveRoadIcon from '@mui/icons-material/RemoveRoad';
import SpeedIcon from '@mui/icons-material/Speed'; // For Efficiency
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // For info messages

const StatPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%', // Keep height 100% for StatPaper itself to fill its grid/stack space if needed
  boxShadow: 'none',
}));

const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.action.hover, 0.4),
  },
  '& .MuiListItemText-secondary': {
      fontSize: '0.8rem',
  }
}));

// Helper to safely get values and format them
const formatValue = (value, decimals = 2, suffix = '', fallback = 'N/A') => {
  const num = parseFloat(value);
  if (isNaN(num)) return fallback;
  return `${num.toFixed(decimals)}${suffix}`;
};

// Accept sx prop but don't rely on it for scrolling/height anymore
const TripDetails = ({ trip, allTrips, sx }) => {

  if (!trip) {
    return (
      // Apply internal padding and basic structure, merging any passed sx
      <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 200, ...sx }}>
        <InfoOutlinedIcon sx={{ fontSize: 50, color: 'text.secondary', mb: 2 }}/>
        <Typography variant="h6" color="text.secondary" align="center">
          Select a trip to view details
        </Typography>
      </Box>
    );
  }

  const tripIdStr = String(trip.id || '');
  const baseId = tripIdStr.split('-')[0];
  const relatedTrips = allTrips.filter(t => String(t.id || '').split('-')[0] === baseId);
  const dropoffTrips = relatedTrips.filter(t => !t.type?.toLowerCase().includes('backhaul'));
  const backhaulTrips = relatedTrips.filter(t => t.type?.toLowerCase().includes('backhaul'));

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return 'Invalid Date'; }
  };

  const calculateDuration = (start, end) => {
      try {
          const startDate = new Date(start);
          const endDate = new Date(end);
          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 'N/A';
          const diffHours = Math.abs(endDate - startDate) / (1000 * 60 * 60);
          return `${diffHours.toFixed(1)} hours`;
      } catch { return 'N/A'; }
  };

  const calculateEfficiency = (km, fuel) => {
    const numKm = parseFloat(km);
    const numFuel = parseFloat(fuel);
    if (isNaN(numKm) || isNaN(numFuel) || numFuel <= 0) return 'N/A';
    return (numKm / numFuel).toFixed(2);
  };

  const allDropoffSegments = dropoffTrips.flatMap(t => t.segments?.map(segment => ({ segment, tripId: t.id, type: 'dropoff' })) || []);
  const allBackhaulSegments = backhaulTrips.flatMap(t => t.segments?.map(segment => ({ segment, tripId: t.id, type: 'backhaul' })) || []);
  const totalSegments = allDropoffSegments.length + allBackhaulSegments.length;

  const hasCombined = dropoffTrips.length > 0 && backhaulTrips.length > 0;
  let combinedStats = {};
  if (hasCombined) {
     const totalDistance = relatedTrips.reduce((sum, t) => sum + (parseFloat(t.totalKm) || 0), 0);
     const totalFuel = relatedTrips.reduce((sum, t) => sum + (parseFloat(t.totalFuel) || 0), 0);
     const totalEmissions = relatedTrips.reduce((sum, t) => sum + (parseFloat(t.totalEmissions) || 0), 0);
     const totalEmptyKm = relatedTrips.reduce((sum, t) => sum + (parseFloat(t.emptyKm) || 0), 0);
     combinedStats = {
        totalDistance: formatValue(totalDistance, 2, ' km'),
        totalFuel: formatValue(totalFuel, 2, ' L'),
        totalEmissions: formatValue(totalEmissions, 2, ' kg CO2'),
        efficiency: calculateEfficiency(totalDistance, totalFuel) + ' km/L',
        emptyKm: formatValue(totalEmptyKm, 2, ' km'),
     };
  }

  const tripType = trip.type?.toLowerCase().includes('backhaul') ? 'Backhaul' : 'Dropoff';
  const tripColor = tripType === 'Backhaul' ? 'error' : 'primary';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, ...sx }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Avatar sx={{ bgcolor: `${tripColor}.main`, width: 48, height: 48 }}>
            <LocalShippingIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" component="h2" fontWeight="600">
              Trip {baseId}
            </Typography>
             <Chip
                label={hasCombined ? `${tripType} (Part of Combined Trip)` : tripType}
                size="small"
                color={tripColor}
                variant="filled"
                sx={{ mt: 0.5, color: 'common.white', bgcolor: `${tripColor}.main` }}
             />
          </Box>
          {hasCombined && (
            <Chip
              icon={<LinkIcon />}
              label="Dropoff + Backhaul"
              size="small"
              variant="outlined"
              color="info"
              sx={{ ml: 'auto' }}
            />
          )}
        </Box>

        {/* Combined Summary Card */}
        {hasCombined && (
          <SectionCard variant="outlined">
             <CardContent>
                <Typography variant="subtitle1" fontWeight="500" gutterBottom>
                   Combined Trip Summary (All Parts)
                </Typography>
                <Stack spacing={0.5}>
                   <Typography variant="body2">Total Distance: {combinedStats.totalDistance}</Typography>
                   <Typography variant="body2">Total Fuel: {combinedStats.totalFuel}</Typography>
                   <Typography variant="body2">Avg Efficiency: {combinedStats.efficiency}</Typography>
                   <Typography variant="body2">Empty Kilometers: {combinedStats.emptyKm}</Typography>
                   <Typography variant="body2">Total Emissions: {combinedStats.totalEmissions}</Typography>
                </Stack>
             </CardContent>
          </SectionCard>
        )}

        {/* Selected Trip Stats */}
        <Box>
            <Typography variant="subtitle1" fontWeight="500" gutterBottom sx={{ mb: 1.5 }}>
                Stats for Selected Part ({trip.id})
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-around">
                <StatPaper>
                    <RouteIcon color="primary" sx={{ fontSize: 36, mb: 1 }} />
                    <Typography variant="h6" fontWeight="500">{formatValue(trip.totalKm, 2, ' km')}</Typography>
                    <Typography variant="body2" color="text.secondary">Distance</Typography>
                </StatPaper>
                <StatPaper>
                    <LocalGasStationIcon color="error" sx={{ fontSize: 36, mb: 1 }} />
                    <Typography variant="h6" fontWeight="500">{formatValue(trip.totalFuel, 2, ' L')}</Typography>
                    <Typography variant="body2" color="text.secondary">Fuel Used</Typography>
                </StatPaper>
                <StatPaper>
                    <Co2Icon color="warning" sx={{ fontSize: 36, mb: 1 }} />
                    <Typography variant="h6" fontWeight="500">{formatValue(trip.totalEmissions, 2, ' kg')}</Typography>
                    <Typography variant="body2" color="text.secondary">CO₂ Emissions</Typography>
                </StatPaper>
                 <StatPaper>
                    <SpeedIcon color="success" sx={{ fontSize: 36, mb: 1 }} />
                    <Typography variant="h6" fontWeight="500">{calculateEfficiency(trip.totalKm, trip.totalFuel)}</Typography>
                    <Typography variant="body2" color="text.secondary">km / L</Typography>
                </StatPaper>
                <StatPaper>
                    <RemoveRoadIcon color="secondary" sx={{ fontSize: 36, mb: 1 }} />
                    <Typography variant="h6" fontWeight="500">{formatValue(trip.emptyKm, 2, ' km')}</Typography>
                    <Typography variant="body2" color="text.secondary">Empty Run</Typography>
                </StatPaper>
            </Stack>
        </Box>

        {/* Route & Duration */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <SectionCard variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight="500" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <PlaceIcon fontSize="small" color="action" /> Route
                    </Typography>
                    <Typography variant="body2"><strong>From:</strong> {trip.startLocation || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>To:</strong> {trip.endLocation || 'N/A'}</Typography>
                </CardContent>
            </SectionCard>
            <SectionCard variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight="500" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <DateRangeIcon fontSize="small" color="action" /> Duration
                    </Typography>
                    <Typography variant="body2">Start: {formatDate(trip.startTime)}</Typography>
                    <Typography variant="body2">End: {formatDate(trip.endTime)}</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Total: {calculateDuration(trip.startTime, trip.endTime)}
                    </Typography>
                </CardContent>
            </SectionCard>
        </Stack>

        {/* Empty/Round Trip Info */}
        {(trip.emptyKm > 0 || trip.roundTripKm > 0) && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                {trip.emptyKm > 0 && (
                    <SectionCard variant="outlined" sx={{ flex: 1 }}>
                        <CardContent>
                            <Typography variant="subtitle1" fontWeight="500" gutterBottom>Empty Running</Typography>
                            <Typography variant="body2">Distance: {formatValue(trip.emptyKm, 2, ' km')}</Typography>
                            <Typography variant="body2">Fuel: {formatValue(trip.emptyFuel, 2, ' L')}</Typography>
                            <Typography variant="body2">Emissions: {formatValue(trip.emptyEmissions, 2, ' kg CO2')}</Typography>
                        </CardContent>
                    </SectionCard>
                )}
                 {trip.roundTripKm > 0 && (
                    <SectionCard variant="outlined" sx={{ flex: 1 }}>
                        <CardContent>
                            <Typography variant="subtitle1" fontWeight="500" gutterBottom>Round Trip (Calculated)</Typography>
                            <Typography variant="body2">Distance: {formatValue(trip.roundTripKm, 2, ' km')}</Typography>
                            <Typography variant="body2">Fuel: {formatValue(trip.roundTripFuel, 2, ' L')}</Typography>
                            <Typography variant="body2">Efficiency: {calculateEfficiency(trip.roundTripKm, trip.roundTripFuel)} km/L</Typography>
                            <Typography variant="body2">Emissions: {formatValue(trip.roundTripEmissions, 2, ' kg CO2')}</Typography>
                        </CardContent>
                    </SectionCard>
                )}
            </Stack>
        )}

        {/* All Trip Segments Section */}
        {totalSegments > 0 && (
          <Box>
            <Typography variant="h6" fontWeight="500" sx={{ mb: 2 }}>
              All Segments for Trip {baseId}
              <Chip label={`${totalSegments} total`} size="small" variant="outlined" sx={{ ml: 1.5 }}/>
            </Typography>

            {/* Dropoff Segments */}
            {allDropoffSegments.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 3, borderRadius: 1.5, overflow: 'hidden' }}>
                <Typography variant="subtitle2" sx={(theme) => ({
                    bgcolor: alpha(theme.palette.primary.light, 0.1),
                    p: 1.5, pl: 2, borderBottom: '1px solid', borderColor: 'divider'
                })}>
                  <Chip label="Dropoff" size="small" color="primary" sx={{ mr: 1 }}/>
                  Segments ({allDropoffSegments.length})
                </Typography>
                <List disablePadding>
                  {allDropoffSegments.map((item, index) => (
                    <React.Fragment key={`dropoff-segment-${index}`}>
                      {index > 0 && <Divider component="li" variant="middle" />}
                      <StyledListItem>
                        <ListItemText
                          primary={
                            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                               <Typography variant="body2" fontWeight="500" component="span">
                                  Segment {index + 1} {dropoffTrips.length > 1 ? `(${item.tripId})` : ''}
                               </Typography>
                               <Chip icon={<RouteIcon/>} label={formatValue(item.segment.km, 2, ' km')} size="small" variant="outlined" color="primary"/>
                               <Chip icon={<LocalGasStationIcon/>} label={formatValue(item.segment.fuel, 2, ' L')} size="small" variant="outlined" color="error"/>
                               <Chip icon={<Co2Icon/>} label={formatValue(item.segment.emissions, 2, ' kg')} size="small" variant="outlined" color="warning"/>
                            </Stack>
                          }
                          secondary={`From: ${item.segment.from || 'N/A'} | To: ${item.segment.to || 'N/A'}`}
                        />
                      </StyledListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
            {/* Fallback for Dropoff trips with NO segments */}
            {dropoffTrips.length > 0 && allDropoffSegments.length === 0 && (
                <Paper variant="outlined" sx={{ mb: 3, borderRadius: 1.5, overflow: 'hidden' }}>
                   <Typography variant="subtitle2" sx={(theme) => ({
                       bgcolor: alpha(theme.palette.primary.light, 0.1),
                       p: 1.5, pl: 2, borderBottom: '1px solid', borderColor: 'divider'
                   })}>
                     <Chip label="Dropoff" size="small" color="primary" sx={{ mr: 1 }}/>
                     Trip Details (No Segments Found)
                   </Typography>
                   <List disablePadding>
                      {dropoffTrips.map((t, index) => (
                        <React.Fragment key={`dropoff-noseg-${index}`}>
                           {index > 0 && <Divider component="li" variant="middle" />}
                           <StyledListItem>
                              <ListItemText
                                 primary={
                                    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                                       <Typography variant="body2" fontWeight="500" component="span">Trip {t.id}</Typography>
                                       <Chip icon={<RouteIcon/>} label={formatValue(t.totalKm, 2, ' km')} size="small" variant="outlined" color="primary"/>
                                       <Chip icon={<LocalGasStationIcon/>} label={formatValue(t.totalFuel, 2, ' L')} size="small" variant="outlined" color="error"/>
                                       <Chip icon={<Co2Icon/>} label={formatValue(t.totalEmissions, 2, ' kg')} size="small" variant="outlined" color="warning"/>
                                    </Stack>
                                 }
                                 secondary={`From: ${t.startLocation || 'N/A'} | To: ${t.endLocation || 'N/A'} | Date: ${formatDate(t.startTime)}`}
                              />
                           </StyledListItem>
                        </React.Fragment>
                      ))}
                   </List>
                </Paper>
            )}

            {/* Backhaul Segments */}
            {allBackhaulSegments.length > 0 && (
              <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                 <Typography variant="subtitle2" sx={(theme) => ({
                    bgcolor: alpha(theme.palette.error.light, 0.1),
                    p: 1.5, pl: 2, borderBottom: '1px solid', borderColor: 'divider'
                 })}>
                   <Chip label="Backhaul" size="small" color="error" sx={{ mr: 1 }}/>
                   Segments ({allBackhaulSegments.length})
                 </Typography>
                <List disablePadding>
                  {allBackhaulSegments.map((item, index) => (
                    <React.Fragment key={`backhaul-segment-${index}`}>
                      {index > 0 && <Divider component="li" variant="middle" />}
                      <StyledListItem>
                         <ListItemText
                           primary={
                             <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                                <Typography variant="body2" fontWeight="500" component="span">
                                   Segment {index + 1} {backhaulTrips.length > 1 ? `(${item.tripId})` : ''}
                                </Typography>
                                <Chip icon={<RouteIcon/>} label={formatValue(item.segment.km, 2, ' km')} size="small" variant="outlined" color="primary"/>
                                <Chip icon={<LocalGasStationIcon/>} label={formatValue(item.segment.fuel, 2, ' L')} size="small" variant="outlined" color="error"/>
                                <Chip icon={<Co2Icon/>} label={formatValue(item.segment.emissions, 2, ' kg')} size="small" variant="outlined" color="warning"/>
                             </Stack>
                           }
                           secondary={`From: ${item.segment.from || 'N/A'} | To: ${item.segment.to || 'N/A'}`}
                         />
                      </StyledListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
            {/* Fallback for Backhaul trips with NO segments */}
             {backhaulTrips.length > 0 && allBackhaulSegments.length === 0 && (
                <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                   <Typography variant="subtitle2" sx={(theme) => ({
                       bgcolor: alpha(theme.palette.error.light, 0.1),
                       p: 1.5, pl: 2, borderBottom: '1px solid', borderColor: 'divider'
                   })}>
                     <Chip label="Backhaul" size="small" color="error" sx={{ mr: 1 }}/>
                     Trip Details (No Segments Found)
                   </Typography>
                   <List disablePadding>
                      {backhaulTrips.map((t, index) => (
                        <React.Fragment key={`backhaul-noseg-${index}`}>
                           {index > 0 && <Divider component="li" variant="middle" />}
                           <StyledListItem>
                               <ListItemText
                                 primary={
                                    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                                       <Typography variant="body2" fontWeight="500" component="span">Trip {t.id}</Typography>
                                       <Chip icon={<RouteIcon/>} label={formatValue(t.totalKm, 2, ' km')} size="small" variant="outlined" color="primary"/>
                                       <Chip icon={<LocalGasStationIcon/>} label={formatValue(t.totalFuel, 2, ' L')} size="small" variant="outlined" color="error"/>
                                       <Chip icon={<Co2Icon/>} label={formatValue(t.totalEmissions, 2, ' kg')} size="small" variant="outlined" color="warning"/>
                                    </Stack>
                                 }
                                 secondary={`From: ${t.startLocation || 'N/A'} | To: ${t.endLocation || 'N/A'} | Date: ${formatDate(t.startTime)}`}
                              />
                           </StyledListItem>
                        </React.Fragment>
                      ))}
                   </List>
                </Paper>
             )}
          </Box>
        )}

      </Stack>
    </Box>
  );
};

export default TripDetails;